import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, FileText, CheckCircle2, Clock, Package, 
  Truck, Users, Calendar, MapPin, CreditCard, AlertCircle
} from 'lucide-react';
import api from '../../utils/api';

const CustomerEstimatePage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId');
  
  const [job, setJob] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (jobId) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [jobId]);

  const fetchData = async () => {
    try {
      // Fetch job
      const jobResponse = await api.get(`/jobs/${jobId}`);
      setJob(jobResponse.data);
      
      // Fetch estimate
      try {
        const estResponse = await api.get(`/estimates/job/${jobId}`);
        setEstimate(estResponse.data);
      } catch (e) {
        console.log('No estimate found');
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load estimate');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptEstimate = async () => {
    try {
      await api.post(`/estimates/${estimate.id}/accept`);
      toast.success('Estimate accepted! Your move is now confirmed.');
      navigate('/mobile/dashboard');
    } catch (error) {
      toast.error('Failed to accept estimate');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="animate-pulse mx-auto text-primary mb-4" size={48} />
          <p className="text-gray-500">Loading estimate...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-4" data-testid="estimate-not-found">
        <header className="mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/mobile/dashboard')}>
            <ArrowLeft size={20} />
          </Button>
        </header>
        
        <Card className="text-center py-12">
          <CardContent>
            <AlertCircle size={64} className="mx-auto text-orange-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">No Job Selected</h2>
            <p className="text-gray-500 mb-6">Please select a job to view its estimate.</p>
            <Button onClick={() => navigate('/mobile/dashboard')}>
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-4" data-testid="no-estimate">
        <header className="mb-6 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/mobile/dashboard')}>
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-lg font-bold">Estimate</h1>
        </header>
        
        <Card className="text-center py-12">
          <CardContent>
            <Clock size={64} className="mx-auto text-blue-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Estimate Pending</h2>
            <p className="text-gray-500 mb-2">
              Your move details are being reviewed.
            </p>
            <p className="text-sm text-gray-400 mb-6">
              You will receive a notification when your estimate is ready.
            </p>
            <div className="space-y-3">
              <Button variant="outline" onClick={() => navigate(`/mobile/inventory?jobId=${jobId}`)}>
                <Package size={16} className="mr-2" />
                Update Inventory
              </Button>
              <Button onClick={() => navigate('/mobile/dashboard')}>
                Return to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50" data-testid="customer-estimate-page">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/mobile/dashboard')}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-lg font-bold">Your Estimate</h1>
                <p className="text-xs text-gray-500">{job.job_number}</p>
              </div>
            </div>
            <Badge variant={estimate.status === 'accepted' ? 'default' : 'secondary'}>
              {estimate.status}
            </Badge>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4 pb-32">
        {/* Summary Card */}
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5">
          <CardContent className="py-6 text-center">
            <p className="text-sm text-gray-600 mb-2">Total Estimated Cost</p>
            <p className="text-4xl font-bold text-primary">
              £{estimate.total_price?.toFixed(2) || '0.00'}
            </p>
            {estimate.vat_amount && (
              <p className="text-sm text-gray-500 mt-1">
                Inc. VAT: £{estimate.vat_amount?.toFixed(2)}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Move Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin size={18} />
              Move Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">FROM</p>
                <p className="font-medium text-sm">{job.from_address?.formatted_address || job.from_address?.city}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-red-500 mt-2" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">TO</p>
                <p className="font-medium text-sm">{job.to_address?.formatted_address || job.to_address?.city}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Cost Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {estimate.labour_hours > 0 && (
              <div className="flex justify-between items-center py-2 border-b">
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-gray-500" />
                  <span className="text-sm">Labour ({estimate.labour_hours}h @ {estimate.crew_size} crew)</span>
                </div>
                <span className="font-medium">£{estimate.labour_cost?.toFixed(2)}</span>
              </div>
            )}
            
            {estimate.vehicle_type && (
              <div className="flex justify-between items-center py-2 border-b">
                <div className="flex items-center gap-2">
                  <Truck size={16} className="text-gray-500" />
                  <span className="text-sm">Vehicle ({estimate.vehicle_type})</span>
                </div>
                <span className="font-medium">£{estimate.vehicle_cost?.toFixed(2) || '0.00'}</span>
              </div>
            )}
            
            {estimate.packing_cost > 0 && (
              <div className="flex justify-between items-center py-2 border-b">
                <div className="flex items-center gap-2">
                  <Package size={16} className="text-gray-500" />
                  <span className="text-sm">Packing Service</span>
                </div>
                <span className="font-medium">£{estimate.packing_cost?.toFixed(2)}</span>
              </div>
            )}
            
            {estimate.additional_charges?.map((charge, idx) => (
              <div key={idx} className="flex justify-between items-center py-2 border-b">
                <span className="text-sm">{charge.name}</span>
                <span className="font-medium">£{charge.amount?.toFixed(2)}</span>
              </div>
            ))}
            
            <div className="flex justify-between items-center py-2 font-bold text-lg">
              <span>Total</span>
              <span className="text-primary">£{estimate.total_price?.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Schedule */}
        {(job.move_out_date || estimate.scheduled_date) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar size={18} />
                Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium">
                {new Date(estimate.scheduled_date || job.move_out_date).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              {estimate.start_time && (
                <p className="text-sm text-gray-600">
                  Start time: {estimate.start_time}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Notes from company */}
        {estimate.notes && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Notes from Company</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700">{estimate.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Terms */}
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-gray-500">
              By accepting this estimate, you agree to the company terms and conditions. 
              A deposit may be required to confirm your booking.
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Fixed Accept Button */}
      {estimate.status !== 'accepted' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 safe-area-bottom">
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => navigate(`/mobile/chat?jobId=${jobId}`)}
            >
              Questions?
            </Button>
            <Button 
              className="flex-1"
              onClick={handleAcceptEstimate}
            >
              <CheckCircle2 size={18} className="mr-2" />
              Accept Estimate
            </Button>
          </div>
        </div>
      )}

      {estimate.status === 'accepted' && (
        <div className="fixed bottom-0 left-0 right-0 bg-green-50 border-t border-green-200 p-4 safe-area-bottom">
          <div className="flex items-center justify-center gap-2 text-green-700">
            <CheckCircle2 size={20} />
            <span className="font-medium">Estimate Accepted - Move Confirmed!</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerEstimatePage;
